import FeedbackTypeCard from 'components/CreateAssignment/FeedbackTypeCard';
import StudentFeedbackSettingsForm from 'components/CreateAssignment/StudentFeedbackSettingsForm';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { cloneDeep, isEqual, omit, pick } from 'lodash';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';
import {
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { getHTMLWordCount, getLanguageLevelText, getSuggestionLengthText, getToneText } from 'utils/helper';

const getFeedbackTypeName = (assignment, t) => {
    return {
        system: t('feedback-section.system-based-feedback', 'General & Detailed Feedback'),
        rubric: t('feedback-section.rubric-based-feedback', 'Rubric-Based Feedback'),
        templated: t('feedback-section.templated-feedback', 'Smart Feedback Templates'),
        custom: t('feedback-section.custom-feedback', 'Custom Feedback Structure'),
    }[assignment.feedbackSettings.structure];
};

const renderPrettyFeedbackSettings = (assignment, t) => {
    switch (assignment.feedbackSettings.structure) {
        case 'system':
        case 'rubric':
            return (
                <>
                    <Stack direction={'row'} spacing={1}>
                        <Text color={'light'}>
                            {t('feedback-section.settings.suggestion-length', 'Length of each suggestion')}:
                        </Text>
                        <Text>
                            {getSuggestionLengthText(
                                assignment.feedbackSettings.suggestionLength || DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                                t
                            )}
                        </Text>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Text color={'light'}>{t('feedback-settings.language-level.title', 'Language level')}:</Text>
                        <Text>
                            {getLanguageLevelText(
                                assignment.feedbackSettings.languageLevel || DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                                t
                            )}
                        </Text>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Text color={'light'}>{t('feedback-settings.tone.feedback-title', 'Feedback tone')}:</Text>
                        <Text>{getToneText(assignment.feedbackSettings.tone || DEFAULT_FEEDBACK_TONE, t)}</Text>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Text sx={{ flexShrink: 0 }} color={'light'}>
                            {t('additional-instructions', 'Additional instructions')}:
                        </Text>
                        <Text component={'i'} rich>
                            {assignment.feedbackCustomContext}
                        </Text>
                    </Stack>
                </>
            );
        case 'templated':
            return <Text color={'light'}>See advanced feedback settings.</Text>;
        case 'custom':
            return (
                <Text color={'light'} rich>
                    {assignment.feedbackSettings.customInstructions}
                </Text>
            );
        default:
            break;
    }
};

const StudentFeedbackSettingsDialog = memo(({ open, form: data, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({ customInstructions: false, templated: {} });

    const canClickCopy = useMemo(() => {
        if (!form) return false;
        if (form.structure !== data.feedbackSettings.structure) return true;
        if (form.structure === 'system' || form.structure === 'rubric') {
            return !(
                isEqual(
                    pick(form, ['suggestionLength', 'languageLevel', 'tone']),
                    pick(data.feedbackSettings, ['suggestionLength', 'languageLevel', 'tone'])
                ) && form.studentFeedbackCustomContext === data.feedbackCustomContext
            );
        }
        if (form.structure === 'templated') {
            return !isEqual(form.templated, data.feedbackSettings.templated);
        }
        if (form.structure === 'custom') {
            return form.customInstructions !== data.feedbackSettings.customInstructions;
        }
    }, [data.feedbackCustomContext, data.feedbackSettings, form]);

    const handleClose = () => {
        if (form.isDirty) {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit now, any changes you have made to feedback settings will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            onClose();
        }
    };

    const handleClickCopy = () => {
        if (data.feedbackSettings.structure === 'system' || data.feedbackSettings.structure === 'rubric') {
            setForm((current) => ({
                ...current,
                structure: data.feedbackSettings.structure,
                suggestionLength: data.feedbackSettings.suggestionLength,
                languageLevel: data.feedbackSettings.languageLevel,
                tone: data.feedbackSettings.tone,
                studentFeedbackCustomContext: data.feedbackCustomContext,
            }));
        }
        if (data.feedbackSettings.structure === 'templated') {
            setForm((current) => ({
                ...current,
                structure: data.feedbackSettings.structure,
                templated: cloneDeep(data.feedbackSettings.templated),
            }));
        }
        if (data.feedbackSettings.structure === 'custom') {
            setForm((current) => ({
                ...current,
                structure: data.feedbackSettings.structure,
                studentFeedbackCustomContext: data.feedbackCustomContext,
            }));
        }
    };

    const handleChange = (changes) => {
        if (typeof changes === 'function') {
            setForm((current) => {
                const newForm = changes(current);
                return {
                    ...newForm,
                    isDirty: true,
                };
            });
        } else {
            setForm((current) => ({
                ...current,
                ...changes,
                isDirty: true,
            }));
        }
    };

    const validateAll = () => {
        if (form.structure === 'rubric' || form.structure === 'system') {
            if (getHTMLWordCount(form.studentFeedbackCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT) {
                message.error(
                    t(
                        'error.additional-instructions-too-long',
                        'Additional instructions exceeds {{wordCount}} words.',
                        { wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                    )
                );
                return false;
            }
            return true;
        }

        if (form.structure === 'templated') {
            if (!data.rubric) {
                message.error(t('error.create-rubric', 'Please create a rubric to use Smart Feedback Templates.'));
                return false;
            }

            let isValid = true;
            data.rubric.criteria.forEach(({ id: criterionId, options }) => {
                const optionIds = options.map(({ id }) => id);
                optionIds.forEach((optionId) => {
                    if (
                        !form.templated?.[criterionId]?.[optionId] ||
                        form.templated?.[criterionId]?.[optionId] > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT
                    ) {
                        isValid = false;
                        setErrors((current) => ({
                            ...current,
                            templated: {
                                ...current.templated,
                                [criterionId]: { ...current.templated?.[criterionId], [optionId]: true },
                            },
                        }));
                        if (!form.templated?.[criterionId]?.[optionId]) {
                            message.error(t('error.missing-required-fields', 'Please complete all required fields.'));
                        }
                        if (form.templated?.[criterionId]?.[optionId] > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT) {
                            message.error(t('error.word-count-exceeded', 'Word count exceeded.'));
                        }
                    } else {
                        setErrors((current) => ({
                            ...current,
                            templated: {
                                ...current.templated,
                                [criterionId]: { ...current.templated?.[criterionId], [optionId]: false },
                            },
                        }));
                    }
                });
            });

            return isValid;
        }

        if (form.structure === 'custom') {
            if (!form.customInstructions.length) {
                setErrors((current) => ({ ...current, customInstructions: true }));
                message.error(
                    t(
                        'error.missing-instructions-custom-feedback',
                        'Please provide instructions for the custom feedback structure.'
                    )
                );
                return false;
            }

            if (getHTMLWordCount(form.customInstructions) > CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT) {
                setErrors((current) => ({ ...current, customInstructions: true }));
                message.error(
                    t('error.custom-instructions-too-long', 'Custom feedback exceeds {{wordCount}} words.', {
                        wordCount: CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
                    })
                );
                return false;
            }

            setErrors((current) => ({ ...current, customInstructions: false }));
            return true;
        }

        return false;
    };

    const handleClickSave = () => {
        const isValid = validateAll();
        if (!isValid) return;
        onSave(omit(form, ['isDirty']));
        onClose();
    };

    useEffect(() => {
        if (open) {
            setForm({
                ...data.studentFeedbackSettings,
                studentFeedbackCustomContext: data.studentFeedbackCustomContext,
                isDirty: false,
            });
        }
    }, [open, data.studentFeedbackSettings, data.studentFeedbackCustomContext]);

    if (!form) return null;

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('formative-feedback.advanced-settings', 'Advanced Formative Feedback Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={5}>
                    <Stack spacing={1}>
                        <Text variant={'p1'}>
                            {t('feedback-section.feedback-structure-label', 'Feedback Structure')}
                        </Text>
                        <Paper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Text weight={'medium'}>
                                    {t('feedback-section.settings.copy-title', 'Copy Assignment Feedback Settings')}
                                </Text>
                                <Text color={'light'}>
                                    {t(
                                        'feedback-section.settings.copy-description',
                                        'Use the advanced feedback settings already established for this assignment. These will replace the settings listed below.'
                                    )}
                                </Text>
                                <Paper
                                    sx={(theme) => ({ background: theme.palette.ensightful.neutral[50] })}
                                    variant={'outlined'}
                                >
                                    <Stack spacing={3}>
                                        <Text weight={'semibold'}>{getFeedbackTypeName(data, t)}</Text>
                                        <Stack>{renderPrettyFeedbackSettings(data, t)}</Stack>
                                    </Stack>
                                </Paper>
                                <Button
                                    sx={{ width: 'fit-content' }}
                                    disabled={!canClickCopy}
                                    onClick={handleClickCopy}
                                >
                                    {t('common:controls.copy-settings', 'Copy Settings')}
                                </Button>
                            </Stack>
                        </Paper>
                        <HorizontalStack direction={'row'} spacing={5}>
                            <FeedbackTypeCard
                                type={'rubric'}
                                isSelected={form.structure === 'rubric'}
                                onClick={() => handleChange({ structure: 'rubric' })}
                            />
                            <FeedbackTypeCard
                                type={'system'}
                                isSelected={form.structure === 'system'}
                                onClick={() => handleChange({ structure: 'system' })}
                            />
                            <FeedbackTypeCard
                                type={'templated'}
                                isSelected={form.structure === 'templated'}
                                onClick={() => handleChange({ structure: 'templated' })}
                            />
                            <FeedbackTypeCard
                                type={'custom'}
                                isSelected={form.structure === 'custom'}
                                onClick={() => handleChange({ structure: 'custom' })}
                            />
                        </HorizontalStack>
                    </Stack>
                    <StudentFeedbackSettingsForm data={data} form={form} errors={errors} handleChange={handleChange} />
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClickSave}>{t('common:controls.save-settings', 'Save Settings')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const HorizontalStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(0, -6),
        padding: theme.spacing(2, 6, 4, 6),
        overflowX: 'auto',
    };
});

export default StudentFeedbackSettingsDialog;
